import { signal, computed, effect } from "@preact/signals-core";
import { getCookie } from "../utils/knocking";

// Define the cart data structure based on Shopify's cart.js response
export interface CartItem {
  id: number;
  properties: Record<string, any>;
  quantity: number;
  variant_id: number;
  key: string;
  title: string;
  price: number;
  original_price: number;
  discounted_price: number;
  line_price: number;
  original_line_price: number;
  final_price: number;
  final_line_price: number;
  sku: string;
  grams: number;
  vendor: string;
  taxable: boolean;
  product_id: number;
  product_has_only_default_variant: boolean;
  gift_card: boolean;
  url: string;
  featured_image: {
    aspect_ratio: number;
    alt: string;
    height: number;
    url: string;
    width: number;
  };
  image: string;
  handle: string;
  requires_shipping: boolean;
  product_type: string;
  product_title: string;
  product_description: string;
  variant_title: string;
  variant_options: string[];
  options_with_values: Array<{
    name: string;
    value: string;
  }>;
  line_level_discount_allocations: any[];
  line_level_total_discount: number;
}

export interface ShopifyCart {
  token: string;
  note: string | null;
  attributes: Record<string, any>;
  original_total_price: number;
  total_price: number;
  total_discount: number;
  total_weight: number;
  item_count: number;
  items: CartItem[];
  requires_shipping: boolean;
  currency: string;
  items_subtotal_price: number;
  cart_level_discount_applications: any[];
}

// ─── Cache Configuration ──────────────────────────────────────────────────────

interface CartCache {
  data: ShopifyCart;
  timestamp: number;
  version: string;
}

const CACHE_KEY = "cart_cache_v1";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const DEBUG_CACHE_KEY = "debug_cart_state";
const DEBUG_TIMESTAMP_KEY = "debug_cart_timestamp";

// Global debug flag (can be toggled via window.__CART_DEBUG = true)
let isDebugEnabled = false;
if (typeof window !== "undefined") {
  (window as any).__CART_DEBUG = isDebugEnabled;
  Object.defineProperty(window, "__CART_DEBUG", {
    get: () => isDebugEnabled,
    set: (value: boolean) => {
      isDebugEnabled = value;
      console.log(`[cart-store] Debug logging ${value ? "enabled" : "disabled"}`);
    },
  });
}

const debugLog = (...args: any[]) => {
  if (isDebugEnabled) {
    console.log(...args);
  }
};

// Signals for cart state
export const cartData = signal<ShopifyCart | null>(null);
export const isCartLoading = signal(false);
export const cartError = signal<string | null>(null);
export const isCartOpen = signal(false);

// Computed values (automatically update when cartData changes)
export const itemCount = computed(() => cartData.value?.item_count || 0);
export const totalPrice = computed(() => cartData.value?.total_price || 0);
export const isEmpty = computed(() => itemCount.value === 0);

// Smart loading indicator - only show if truly loading (no cache available)
export const shouldShowLoading = computed(
  () => isCartLoading.value && cartData.value === null
);

// ─── Limit types ──────────────────────────────────────────────────────────────

export interface LimitStatus {
  /** Which rule applied. "none" means no limit is configured for this product. */
  type: "product" | "brand" | "global" | "none";
  /** The configured maximum quantity. 0 when type is "none". */
  limit: number;
  /** Units of this product/brand already in the cart. */
  inCart: number;
  /** How many more can still be added. Infinity when type is "none". */
  remaining: number;
  /** True when inCart >= limit. */
  reached: boolean;
}

export interface LimitContext {
  productId: number;
  vendor: string;
}

// ─── Internal quantity aggregates (reactive, updated on every cart change) ────

const productQtyInCart = computed<Map<number, number>>(() => {
  const map = new Map<number, number>();
  for (const item of cartData.value?.items ?? []) {
    map.set(item.product_id, (map.get(item.product_id) ?? 0) + item.quantity);
  }
  return map;
});

const brandQtyInCart = computed<Map<string, number>>(() => {
  const map = new Map<string, number>();
  for (const item of cartData.value?.items ?? []) {
    const vendor = decodeURIComponent(item.vendor.trim());
    map.set(vendor, (map.get(vendor) ?? 0) + item.quantity);
  }
  return map;
});

// ─── Public limit API ─────────────────────────────────────────────────────────

/**
 * Returns the current limit status for a product.
 * Priority: product limit → brand limit → global limit → none.
 * Call this inside a signal effect or computed to get reactivity.
 */
export const getLimitStatus = (
  productId: number,
  vendor: string
): LimitStatus => {
  const w = window as any;
  const pid = productId.toString();
  const v = decodeURIComponent(vendor.trim());

  const productLimit: number | undefined = w.productLimits?.[pid];
  if (productLimit !== undefined) {
    const inCart = productQtyInCart.value.get(productId) ?? 0;
    const remaining = productLimit - inCart;
    return {
      type: "product",
      limit: productLimit,
      inCart,
      remaining,
      reached: remaining <= 0,
    };
  }

  const brandLimit: number | undefined = w.brandLimits?.[v];
  if (brandLimit !== undefined) {
    const inCart = brandQtyInCart.value.get(v) ?? 0;
    const remaining = brandLimit - inCart;
    return {
      type: "brand",
      limit: brandLimit,
      inCart,
      remaining,
      reached: remaining <= 0,
    };
  }

  if (w.enableGlobalProductLimit) {
    const globalLimit: number = w.globalProductLimit;
    const inCart = productQtyInCart.value.get(productId) ?? 0;
    const remaining = globalLimit - inCart;
    return {
      type: "global",
      limit: globalLimit,
      inCart,
      remaining,
      reached: remaining <= 0,
    };
  }

  return {
    type: "none",
    limit: 0,
    inCart: 0,
    remaining: Infinity,
    reached: false,
  };
};

/**
 * Computed set of product IDs whose limit is currently reached.
 * Derived from items already in the cart — updates automatically on cart change.
 * Useful for disabling add-to-cart buttons on collection/product pages.
 */
export const limitReachedProductIds = computed<Set<number>>(() => {
  const set = new Set<number>();
  for (const item of cartData.value?.items ?? []) {
    if (getLimitStatus(item.product_id, item.vendor).reached) {
      set.add(item.product_id);
    }
  }
  return set;
});

/**
 * Set when addToCart is blocked by a limit. Auto-clears after 3 s.
 * Components subscribe to this to show the limit notice.
 */
export const cartLimitError = signal<LimitStatus | null>(null);

let _limitErrorTimer: ReturnType<typeof setTimeout> | null = null;

const setLimitError = (status: LimitStatus): void => {
  if (_limitErrorTimer !== null) clearTimeout(_limitErrorTimer);
  cartLimitError.value = status;
  _limitErrorTimer = setTimeout(() => {
    cartLimitError.value = null;
    _limitErrorTimer = null;
  }, 3000);
};

// ─── Cross-selling: variant IDs dismissed by the user in this session ─────────

const DELETED_VARIANTS_STORAGE_KEY = "deleted_cross_sell_variant_ids";

/**
 * Load discarded cross-sell variant IDs from sessionStorage.
 * Falls back to an empty array on parse errors or missing key.
 */
const loadDeletedVariantIdsFromStorage = (): number[] => {
  try {
    const raw = sessionStorage.getItem(DELETED_VARIANTS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as number[]) : [];
  } catch {
    return [];
  }
};

export const deletedVariantIds = signal<number[]>(loadDeletedVariantIdsFromStorage());

/**
 * Permanently discard a cross-sell variant id for the current tab session.
 * Deduplicates and updates sessionStorage so the exclusion survives
 * same-tab navigations.
 */
export const addToDeletedVariants = (id: number): void => {
  const current = deletedVariantIds.value;
  if (current.includes(id)) return;
  const next = [...current, id];
  deletedVariantIds.value = next;
  try {
    sessionStorage.setItem(DELETED_VARIANTS_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Silently ignore storage errors (e.g. QuotaExceededError)
  }
};

// ─── SessionStorage Cache Helpers ─────────────────────────────────────────────

/**
 * Load cart from sessionStorage cache.
 * Returns null if cache doesn't exist, is invalid, or expired.
 */
const loadCartFromCache = (): CartCache | null => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) {
      debugLog("[cart-store] No cache found");
      return null;
    }

    const cache: CartCache = JSON.parse(cached);
    debugLog("[cart-store] Cache loaded:", {
      itemCount: cache.data?.item_count,
      timestamp: new Date(cache.timestamp).toISOString(),
      age: `${Math.round((Date.now() - cache.timestamp) / 1000)}s`,
    });

    return cache;
  } catch (error) {
    console.warn("[cart-store] Failed to load cache:", error);
    return null;
  }
};

/**
 * Check if cache is still valid (within TTL).
 */
const isCacheValid = (cache: CartCache | null): boolean => {
  if (!cache) return false;
  const age = Date.now() - cache.timestamp;
  const isValid = age < CACHE_TTL;
  debugLog("[cart-store] Cache validation:", {
    age: `${Math.round(age / 1000)}s`,
    ttl: `${CACHE_TTL / 1000}s`,
    valid: isValid,
  });
  return isValid;
};

/**
 * Save cart to sessionStorage cache.
 */
const saveCartToCache = (cart: ShopifyCart): void => {
  try {
    const cache: CartCache = {
      data: cart,
      timestamp: Date.now(),
      version: "1",
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    debugLog("[cart-store] ✓ Cart saved to cache:", {
      itemCount: cart.item_count,
      totalPrice: cart.total_price,
    });
  } catch (error) {
    if (error instanceof Error && error.name === "QuotaExceededError") {
      console.warn("[cart-store] SessionStorage full, clearing and retrying");
      sessionStorage.clear();
      try {
        const cache: CartCache = {
          data: cart,
          timestamp: Date.now(),
          version: "1",
        };
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      } catch (retryError) {
        console.warn("[cart-store] Failed to save cache after clear:", retryError);
      }
    } else {
      console.warn("[cart-store] Failed to save cache:", error);
    }
  }
};

/**
 * Clear the cart cache.
 */
const clearCartCache = (): void => {
  try {
    sessionStorage.removeItem(CACHE_KEY);
    debugLog("[cart-store] Cache cleared");
  } catch (error) {
    console.warn("[cart-store] Failed to clear cache:", error);
  }
};

/**
 * Check if cart data has changed (for detecting updates from server).
 */
const hasCartChanged = (
  cached: ShopifyCart | null,
  fresh: ShopifyCart
): boolean => {
  if (!cached) return true;

  const changed =
    cached.token !== fresh.token ||
    cached.item_count !== fresh.item_count ||
    cached.total_price !== fresh.total_price;

  if (changed) {
    debugLog("[cart-store] Cart changed:", {
      cached: {
        token: cached.token,
        itemCount: cached.item_count,
        totalPrice: cached.total_price,
      },
      fresh: {
        token: fresh.token,
        itemCount: fresh.item_count,
        totalPrice: fresh.total_price,
      },
    });
  }

  return changed;
};

// Track latest refresh to ignore stale responses
let latestRefreshTimestamp = 0;

/**
 * Fetch cart data from Shopify and update cache.
 * Uses stale-while-revalidate pattern when called from initCart.
 */
export const refreshCart = async (isBackgroundRefresh = false): Promise<void> => {
  // Only set loading state if this is not a background refresh
  if (!isBackgroundRefresh) {
    isCartLoading.value = true;
  }

  cartError.value = null;

  // Track this refresh timestamp to ignore stale responses
  const thisRefresh = Date.now();
  latestRefreshTimestamp = thisRefresh;

  debugLog("[cart-store] Starting cart refresh:", {
    background: isBackgroundRefresh,
    hasCache: cartData.value !== null,
  });

  try {
    const rootUrl = (window as any).Shopify?.routes?.root || "/";
    const res = await fetch(`${rootUrl}cart.js`);

    if (!res.ok) {
      throw new Error(`Failed to fetch cart: ${res.statusText}`);
    }

    const freshData: ShopifyCart = await res.json();

    // Only update if this is still the latest request
    if (thisRefresh !== latestRefreshTimestamp) {
      debugLog("[cart-store] Ignoring stale response");
      return;
    }

    debugLog("[cart-store] Fresh data received:", {
      itemCount: freshData.item_count,
      totalPrice: freshData.total_price,
    });

    // Check if cart actually changed
    if (hasCartChanged(cartData.value, freshData)) {
      debugLog("[cart-store] Cart updated from server");
      cartData.value = freshData;
      saveCartToCache(freshData);
    } else {
      debugLog("[cart-store] No changes detected, cache is current");
    }
  } catch (error) {
    console.error("[cart-store] Error fetching cart:", error);
    cartError.value = error instanceof Error ? error.message : "Unknown error";

    if (isBackgroundRefresh) {
      debugLog("[cart-store] Background refresh failed, keeping cached data");
      // Keep cached data, will retry on next navigation or action
    }
  } finally {
    isCartLoading.value = false;
  }
};

// ─── Shared cart-add gateway ──────────────────────────────────────────────────

let templateName = "unknown";

/**
 * Single entry point for every POST /cart/add.js call.
 * Add GA tracking, analytics, or other side effects here.
 */
const executeCartAdd = async (
  variantId: number,
  quantity: number,
  properties?: Record<string, any>,
  analytics?: { source?: string; productId?: number }
): Promise<void> => {
  // Collect promo code from cookie
  const promoProps: Record<string, string> = {};
  const promoCode = getCookie("promo_code");
  if (promoCode?.trim()) {
    promoProps["_promo-code"] = promoCode;
  }

  // Collect referral properties from sessionStorage and merge with incoming properties
  const referralProps: Record<string, string> = {};
  const renamingMap: Record<string, string> = (window as any).referalRenaming ?? {};
  for (const [key, value] of Object.entries(sessionStorage)) {
    if (key.startsWith("referals-")) {
      const rawKey = key.replace("referals-", "");
      const propName = renamingMap[rawKey] ?? rawKey;
      referralProps[`_${propName}`] = value;
    }
  }

  const mergedProperties = { ...promoProps, ...referralProps, ...properties };
  const body: Record<string, any> = { id: variantId, quantity };
  if (Object.keys(mergedProperties).length > 0) {
    body.properties = mergedProperties;
  }

  const baseAddUrl = (window as any).routes?.cart_add_url || "/cart/add";
  // Force the AJAX JSON endpoint (`.js`) so Shopify emits the standard
  // `product_added_to_cart` web-pixel event. The bare `/cart/add` route is
  // treated as a form add (302 redirect) and the interceptor never fires.
  // Guard against a value that already ends in `.js`/`.json`.
  const addUrl = /\.(js|json)$/.test(baseAddUrl) ? baseAddUrl : `${baseAddUrl}.js`;
  const res = await fetch(addUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(`Failed to add to cart: ${res.statusText}`);
  }

  // One successful POST adds exactly one cart line, and Shopify emits exactly
  // one `product_added_to_cart` web-pixel event per line. Publishing `kn` here
  // keeps the two events 1:1 so they map cleanly in GA — a multi-line add
  // (customizable product's companion SKU, or a split-mode voucher's per-code
  // lines) fires both events the same number of times. This relies on sending
  // ONE item per POST: do not batch into a single `items:[…]` request, or the
  // per-call `kn` and per-line `product_added_to_cart` would diverge.
  //
  // Klaviyo "Added to Cart" is deliberately NOT fired here — its payload is the
  // whole cart, so it is published once per user action at the add boundary via
  // publishKlaviyoAddedToCart() (modal-store executeAddToCart, cross-selling-cart).
  publishKnAddToCart(analytics?.source, variantId, analytics?.productId);
};

/**
 * Push an "Added to Cart" event to Klaviyo's onsite tracking queue (`_learnq`).
 * Mirrors the legacy behaviour in assets/product-form.js: prices are converted
 * from cents to dollars, while `total_discount` is intentionally left in cents
 * to preserve the existing payload shape.
 */
const trackKlaviyoAddedToCart = (cart: ShopifyCart | null): void => {
  if (!cart) return;

  const _learnq = ((window as any)._learnq = (window as any)._learnq || []);

  const klaviyoCart = {
    total_price: cart.total_price / 100,
    $value: cart.total_price / 100,
    total_discount: cart.total_discount,
    original_total_price: cart.original_total_price / 100,
    items: cart.items,
  };

  console.log('[cart-store] Klaviyo "Added to Cart" tracked:', klaviyoCart);
  _learnq.push(["track", "Added to Cart", klaviyoCart]);
};

/**
 * Publish the `kn_add_to_cart` custom pixel event (forwarded to GA) for a single
 * cart line.
 *
 * Called once per line from executeCartAdd so it stays 1:1 with Shopify's
 * per-line `product_added_to_cart` standard event — the whole point of this
 * event is to tag each Shopify add with the CTA `source` for GA mapping, which
 * only works if the two fire the same number of times. Do NOT call this at the
 * user-action boundary: a multi-line add (companion SKU, split-mode voucher)
 * must fire it per line to match.
 */
export const publishKnAddToCart = (
  source?: string,
  variantId?: number,
  productId?: number
): void => {
  try {
    const payload = {
      source: `${source ?? "unknown"}_${templateName}-page`,
      variantId,
      productId,
    };
    console.log("[cart-store] Publishing analytics payload:", payload);
    (window as any).Shopify?.analytics?.publish("kn_add_to_cart", payload);
  } catch (error) {
    console.error("[cart-store] Failed to publish analytics payload:", error);
  }
};

/**
 * Push the Klaviyo "Added to Cart" onsite event for a single user action.
 *
 * Unlike `kn_add_to_cart`, this must fire ONCE per add — not per line item —
 * because its payload is the whole cart (total, value, all items). Firing it per
 * line would send duplicate whole-cart events and inflate Klaviyo's add-to-cart
 * count and value. Boundaries that represent one user intent (modal-store
 * executeAddToCart, cross-selling-cart) call this exactly once after the whole
 * add succeeds, so the cart total reflects everything just added.
 */
export const publishKlaviyoAddedToCart = (): void => {
  trackKlaviyoAddedToCart(cartData.value);
};

/**
 * Identify the current browser to Klaviyo by email so onsite `track` events
 * (e.g. "Added to Cart") are attributed to the customer's profile. Klaviyo only
 * records track events for a cookied/identified browser; identifying early on
 * page load sets the __kla_id cookie and sidesteps the identify→track race.
 */
export const publishKlaviyoIdentify = (email: string): void => {
  if (!email) return;
  const _learnq = ((window as any)._learnq = (window as any)._learnq || []);
  _learnq.push(["identify", { $email: email }]);
};

// Function to add item to cart.
// Returns true when the item was added, false when the add was blocked
// (e.g. by a purchase limit) or failed — callers should not open the cart
// when this resolves to false.
export const addToCart = async (
  variantId: number,
  quantity: number = 1,
  properties?: Record<string, any>,
  limitContext?: LimitContext,
  analytics?: { source?: string; productId?: number }
): Promise<boolean> => {
  console.log("Attempting add to cart: ", {
    variantId,
    quantity,
    properties,
    limitContext,
  });
  if (limitContext) {
    const status = getLimitStatus(limitContext.productId, limitContext.vendor);
    if (status.type !== "none" && status.remaining < quantity) {
      setLimitError(status);
      return false;
    }
  }

  isCartLoading.value = true;
  cartError.value = null;

  try {
    await executeCartAdd(variantId, quantity, properties, analytics);

    // Refresh cart after adding
    await refreshCart();

    return true;
  } catch (error) {
    console.error("Error adding to cart:", error);
    cartError.value = error instanceof Error ? error.message : "Unknown error";
    isCartLoading.value = false;
    return false;
  }
};

// Function to update cart item quantity
export const updateCartItem = async (
  line: number,
  quantity: number
): Promise<void> => {
  isCartLoading.value = true;
  cartError.value = null;

  try {
    const res = await fetch(
      (window as any).routes?.cart_change_url || "/cart/change.js",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          line,
          quantity,
        }),
      }
    );

    if (!res.ok) {
      throw new Error(`Failed to update cart: ${res.statusText}`);
    }

    await refreshCart();

    // Keep voucher-code arrays in step with the new line quantity (merge mode).
    // Guarded so non-voucher carts skip the dynamic import entirely.
    const { hasVoucherCodesOutOfSync } = await import("./voucher-store");
    if (hasVoucherCodesOutOfSync()) {
      const { reconcileVoucherCodes } = await import("./voucher-store");
      await reconcileVoucherCodes();
    }
  } catch (error) {
    console.error("Error updating cart:", error);
    cartError.value = error instanceof Error ? error.message : "Unknown error";
    isCartLoading.value = false;
  }
};

/**
 * Update a single line item's property (and quantity) via /cart/change.js.
 * Used by voucher reconciliation to rewrite the `_encoded-vouchers` array.
 */
export const updateCartProperty = async (
  line: number,
  property: string,
  value: any,
  quantity: number
): Promise<void> => {
  try {
    const res = await fetch(
      (window as any).routes?.cart_change_url || "/cart/change.js",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          line,
          quantity,
          properties: { [property]: value },
        }),
      }
    );

    if (!res.ok) {
      throw new Error(`Failed to update cart property: ${res.statusText}`);
    }
  } catch (error) {
    console.error("Error updating cart property:", error);
    cartError.value = error instanceof Error ? error.message : "Unknown error";
  }
};

// Actions to open/close the cart drawer
export const openCart = (): void => {
  isCartOpen.value = true;
};

export const closeCart = (): void => {
  isCartOpen.value = false;
};

// Convenience wrapper: set quantity to 0 to remove the line item
export const removeCartItem = async (line: number): Promise<void> => {
  await updateCartItem(line, 0);
};

// Function to clear cart
export const clearCart = async (): Promise<void> => {
  isCartLoading.value = true;
  cartError.value = null;

  try {
    const res = await fetch(
      (window as any).routes?.cart_clear_url || "/cart/clear.js",
      {
        method: "POST",
      }
    );

    if (!res.ok) {
      throw new Error(`Failed to clear cart: ${res.statusText}`);
    }

    await refreshCart();
  } catch (error) {
    console.error("Error clearing cart:", error);
    cartError.value = error instanceof Error ? error.message : "Unknown error";
    isCartLoading.value = false;
  }
};

// ─── Debug: Session Storage Sync ──────────────────────────────────────────────

let sessionStorageDebounce: number | null = null;

/**
 * Save cart state to sessionStorage for debugging purposes.
 * This runs asynchronously and debounced to avoid performance impact.
 * Uses separate keys from the cache to avoid conflicts.
 */
const saveCartToSessionStorage = () => {
  debugLog('[cart-store] saveCartToSessionStorage called');

  if (sessionStorageDebounce) {
    clearTimeout(sessionStorageDebounce);
  }

  sessionStorageDebounce = window.setTimeout(() => {
    debugLog('[cart-store] Debounce timer fired, saving debug data');
    try {
      if (cartData.value) {
        const cartJson = JSON.stringify(cartData.value, null, 2);
        sessionStorage.setItem(DEBUG_CACHE_KEY, cartJson);
        sessionStorage.setItem(DEBUG_TIMESTAMP_KEY, new Date().toISOString());
        debugLog('[cart-store] ✓ Debug data saved to sessionStorage:', {
          itemCount: cartData.value.item_count,
          totalPrice: cartData.value.total_price,
        });
      }
    } catch (error) {
      console.warn('[cart-store] Failed to save debug data:', error);
    }
  }, 500); // 500ms debounce
};

/**
 * Initialize cart store with stale-while-revalidate pattern.
 * 
 * Flow:
 * 1. Try to load from sessionStorage cache (instant)
 * 2. If cache is valid, show it immediately
 * 3. Fetch fresh data from Shopify in background
 * 4. Update if changed
 */
export const initCart = (): void => {
  const init = () => {
    templateName = document.body.dataset.template ?? "unknown";
    debugLog("[cart-store] Initializing cart store");

    // 1. Try to load from cache first (instant, 0ms)
    const cached = loadCartFromCache();
    if (cached && isCacheValid(cached)) {
      debugLog("[cart-store] Using valid cache for instant display");
      cartData.value = cached.data;

      // 2. Fetch fresh data in background (don't await)
      debugLog("[cart-store] Starting background refresh");
      refreshCart(true).catch((error) => {
        console.error("[cart-store] Background refresh failed:", error);
      });
    } else {
      // No valid cache, show loading and fetch
      if (cached) {
        debugLog("[cart-store] Cache expired, fetching fresh data");
      } else {
        debugLog("[cart-store] No cache, fetching fresh data");
      }
      refreshCart(false); // Not background, show loading
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Setup debug sessionStorage sync (always enabled)
  effect(() => {
    debugLog("[cart-store] Effect triggered, cartData:", cartData.value ? "exists" : "null");
    if (cartData.value) {
      saveCartToSessionStorage();
    }
  });

  // Publish to legacy messenger so cart-bubble.js and other listeners stay in sync
  effect(() => {
    const count = itemCount.value;
    const messenger = (window as any).messenger;
    if (messenger?.emmit) {
      messenger.emmit(messenger.topics.UPDATE_CART_BUBBLE, count);
    }
  });

  debugLog("[cart-store] Debug sessionStorage sync enabled");
};
